import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import Select from 'react-select'

import PosSearchSelect from 'pages/parts/tasks/PosSearchSelect'
import PartnerSearchSelect from 'pages/parts/tasks/PartnerSearchSelect'

import { formatDate, parseLocalDateInput } from 'helpers/helpers'

// Категории приходят без цвета, поэтому раскрашиваем чипы по индексу
const CATEGORY_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#06b6d4', '#ec4899']

// Данные клиента заполняют только на подключении: точки в системе ещё нет
const CONNECTION_CATEGORY_CODE = 'connection'

// Источники лида — короткий фиксированный список, справочник на бэке ради него не заводим
const LEAD_SOURCE_OPTIONS = [
	'Instagram',
	'Telegram',
	'Facebook',
	'Сайт',
	'Входящий звонок',
	'Холодный звонок',
	'Рекомендация',
	'Партнёр',
	'Выставка',
	'Другое',
].map(item => ({ 'value': item, 'label': item }))

const selectStyles = {
	control: (base, state) => ({
		...base,
		backgroundColor: state.isDisabled ? 'var(--dark-surface-2, #f4f6f9)' : 'var(--dark-surface, #fff)',
		borderColor: state.isFocused ? '#5b73e8' : 'var(--dark-tint-border-primary, #dbe1ea)',
		borderRadius: '10px',
		boxShadow: state.isFocused ? '0 0 0 3px rgba(91, 115, 232, .12)' : 'none',
		minHeight: '42px',
		':hover': { borderColor: state.isFocused ? '#5b73e8' : 'var(--dark-tint-border-primary, #c3cbd8)' },
	}),
	valueContainer: (base) => ({ ...base, gap: '4px', padding: '4px 10px' }),
	placeholder: (base) => ({ ...base, color: 'var(--dark-text-muted, #aeb6c2)', fontSize: '13px' }),
	singleValue: (base) => ({ ...base, color: 'var(--dark-text-strong, #111827)', fontSize: '13.5px' }),
	input: (base) => ({ ...base, margin: 0, padding: 0 }),
	menu: (base) => ({
		...base,
		borderRadius: '10px',
		boxShadow: '0 8px 24px rgba(15, 23, 42, .12)',
		overflow: 'hidden',
		zIndex: 5,
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isSelected ? '#5b73e8' : (state.isFocused ? 'var(--dark-tint-primary, #eef2ff)' : 'var(--dark-surface, #fff)'),
		color: state.isSelected ? '#fff' : 'var(--dark-text, #374151)',
		fontSize: '13.5px',
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: 'var(--dark-surface, #fff)',
		border: '1px solid var(--dark-tint-border-primary, #cdd5e4)',
		borderRadius: '7px',
		margin: 0,
		overflow: 'hidden',
	}),
	multiValueLabel: (base) => ({
		...base,
		color: 'var(--dark-text, #3b4a6b)',
		fontSize: '12.5px',
		fontWeight: 600,
		padding: '3px 4px 3px 8px',
	}),
	multiValueRemove: (base) => ({
		...base,
		color: 'var(--dark-text-muted, #8b97ad)',
		paddingLeft: '4px',
		paddingRight: '6px',
		':hover': { backgroundColor: 'var(--dark-tint-danger, #fdecec)', color: '#f46a6a' },
	}),
	indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--dark-surface-4, #e5e9f0)' }),
	dropdownIndicator: (base) => ({ ...base, color: 'var(--dark-text-muted, #a3adba)', ':hover': { color: '#5b73e8' } }),
	clearIndicator: (base) => ({ ...base, color: 'var(--dark-text-muted, #a3adba)', ':hover': { color: '#f46a6a' } }),
}

function ModalCreateUpdate({
	apiPrefix,
	categories = [],
	partners = [],
	currentUser = null,
	// Состав исполнителей меняет только автор задачи — остальным селект показываем, но запертым
	canManageExecutors = true,
	selectedItem,
	setSelectedItem,
	createUpdate,
	modalCreateUpdate,
	toggleModalCreateUpdate,
}) {
	const { t } = useTranslation()

	// На задачу одна точка: логин клиента подтягивается из неё,
	// pos_id уходит на бэк, pos остаётся объектом опции для селекта
	function handleSelectPos(option) {
		setSelectedItem({
			...selectedItem,
			'client_login': option ? option.login : null,
			'pos_id': option ? option.id : null,
			'pos': option || null,
		})
	}

	// Автор всегда исполнитель, поэтому в селекте оставляем только «кому ещё»
	function returnExecutorOptions() {
		if (!currentUser) return partners
		return partners.filter(item => item.id !== currentUser.id)
	}

	function returnExecutorValue() {
		const selected = selectedItem.user_ids || []
		if (!currentUser) return selected
		return selected.filter(item => item.id !== currentUser.id)
	}

	// Блок нужен только на подключении. Но если данные уже заполнены (категорию сменили
	// после создания), поле прячем не молча — иначе сохранится то, чего не видно
	function showClientData() {
		const category = categories.find(item => item.id === selectedItem.category_id)
		if (category && category.code === CONNECTION_CATEGORY_CODE) return true

		return Boolean(
			selectedItem.client_phone ||
			selectedItem.director_phone ||
			selectedItem.address ||
			selectedItem.direction ||
			selectedItem.lead_source
		)
	}

	function returnLeadSourceValue() {
		if (!selectedItem.lead_source) return null
		return { 'value': selectedItem.lead_source, 'label': selectedItem.lead_source }
	}

	function isPastDeadline() {
		if (!selectedItem.deadline) return false
		return selectedItem.deadline < formatDate(new Date(), 'yyyy-MM-dd')
	}

	function handleKeyDown(e) {
		if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && selectedItem.name) {
			createUpdate(e)
		}
	}

	return (
		<Modal show={modalCreateUpdate} animation={false} centered
			dialogClassName="update-modal-width tasks-modal"
			onHide={() => toggleModalCreateUpdate()}>
			<Modal.Header closeButton>
				<div>
					<div className="tasks-modal-title">
						{!selectedItem.id ? t('task_new_title') : t('task_edit_title')}
					</div>
					{!selectedItem.id &&
						<div className="tasks-modal-subtitle">{t('task_new_modal_hint')}</div>
					}
				</div>
			</Modal.Header>
			<Modal.Body>
				<form onSubmit={(e) => createUpdate(e)} onKeyDown={(e) => handleKeyDown(e)} autoComplete="off">

					{categories.length > 0 &&
						<div className="mb-3">
							<label className="tasks-modal-label">{t('task_type')}</label>
							<div className="tasks-type-chips">
								{categories.map((item, index) => {
									const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length]
									const isActive = selectedItem.category_id === item.id
									return (
										<button type="button" key={item.id}
											className={'tasks-type-chip' + (isActive ? ' active' : '')}
											style={isActive ? { borderColor: color, background: color + '14' } : {}}
											onClick={() => setSelectedItem({
												...selectedItem,
												'category_id': isActive ? null : item.id,
											})}>
											<span className="tasks-type-chip-dot" style={{ background: color }}></span>
											{item.name}
										</button>
									)
								})}
							</div>
						</div>
					}

					<div className="mb-3">
						<label className="tasks-modal-label">
							{t('task_title')}<span className="required-mark">*</span>
						</label>
						<input type="text" className="form-control" name="name" autoFocus
							placeholder={t('task_title_placeholder')}
							value={selectedItem.name || ''}
							onChange={(e) => setSelectedItem({ ...selectedItem, 'name': e.target.value })} />
					</div>

					<div className="mb-3">
						<label className="tasks-modal-label">{t('pos')}</label>
						<PosSearchSelect
							apiPrefix={apiPrefix}
							styles={selectStyles}
							value={selectedItem.pos || ''}
							onChange={(option) => handleSelectPos(option)}
							placeholder={t('task_pos_search_placeholder')}>
						</PosSearchSelect>
					</div>

					{/* На подключении точки в системе ещё нет, поэтому данные клиента вводятся руками */}
					{showClientData() &&
					<div className="tasks-modal-section">
						<div className="tasks-modal-section-title">
							{t('task_client_data')}
							<span className="tasks-modal-section-hint">{t('task_client_data_hint')}</span>
						</div>

						<div className="row">
							<div className="col-md-6 mb-3">
								<label className="tasks-modal-label">{t('task_client_phone')}</label>
								<input type="text" className="form-control" name="client_phone"
									placeholder={t('task_phone_placeholder')}
									value={selectedItem.client_phone || ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'client_phone': e.target.value })} />
							</div>
							<div className="col-md-6 mb-3">
								<label className="tasks-modal-label">{t('task_director_phone')}</label>
								<input type="text" className="form-control" name="director_phone"
									placeholder={t('task_phone_placeholder')}
									value={selectedItem.director_phone || ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'director_phone': e.target.value })} />
							</div>
						</div>

						<div className="mb-3">
							<label className="tasks-modal-label">{t('address')}</label>
							<input type="text" className="form-control" name="address"
								placeholder={t('task_address_placeholder')}
								value={selectedItem.address || ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'address': e.target.value })} />
						</div>

						<div className="row">
							<div className="col-md-6 mb-3">
								<label className="tasks-modal-label">{t('task_direction')}</label>
								<input type="text" className="form-control" name="direction"
									placeholder={t('task_direction_placeholder')}
									value={selectedItem.direction || ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'direction': e.target.value })} />
							</div>
							<div className="col-md-6 mb-3">
								<label className="tasks-modal-label">{t('task_lead_source')}</label>
								<Select
									isClearable
									styles={selectStyles}
									options={LEAD_SOURCE_OPTIONS}
									value={returnLeadSourceValue()}
									onChange={(option) => setSelectedItem({
										...selectedItem,
										'lead_source': option ? option.value : null,
									})}
									placeholder={t('task_lead_source_placeholder')}
									noOptionsMessage={() => t('list_empty')}
								/>
							</div>
						</div>
					</div>
					}

					<div className="row">
						<div className="col-md-7 mb-3">
							<label className="tasks-modal-label">{t('task_executors')}</label>
							<PartnerSearchSelect
								apiPrefix={apiPrefix}
								isDisabled={!canManageExecutors}
								styles={selectStyles}
								partners={returnExecutorOptions()}
								excludeId={currentUser ? currentUser.id : null}
								value={returnExecutorValue()}
								onChange={(options) => setSelectedItem({ ...selectedItem, 'user_ids': options })}
								placeholder={currentUser ? t('task_select_more_executors') : t('task_select_executors')}>
							</PartnerSearchSelect>

							<div className="tasks-modal-hint">
								{canManageExecutors ? t('task_executors_hint') : t('task_executors_author_only')}
							</div>
						</div>
						<div className="col-md-5 mb-3">
							<label className="tasks-modal-label">{t('deadline')}</label>
							<div className="tasks-modal-date">
								<i className="uil uil-calendar-alt"></i>
								<DatePicker
									showMonthDropdown
									showYearDropdown
									dropdownMode="select"
									selected={parseLocalDateInput(selectedItem.deadline)}
									onChange={(date) => setSelectedItem({
										...selectedItem,
										'deadline': date ? formatDate(date, 'yyyy-MM-dd') : '',
									})}
									dateFormat="dd.MM.yyyy"
									className="form-control"
									placeholderText="дд.мм.гггг"
								/>
							</div>

							{isPastDeadline() &&
								<div className="tasks-modal-warning">
									<i className="uil uil-exclamation-triangle"></i>
									{t('task_deadline_past_hint')}
								</div>
							}
						</div>
					</div>

					<div className="mb-2">
						<label className="tasks-modal-label">{t('description')}</label>
						<textarea className="form-control" rows={3} name="description"
							placeholder={t('task_description_placeholder')}
							value={selectedItem.description || ''}
							onChange={(e) => setSelectedItem({ ...selectedItem, 'description': e.target.value })} />
					</div>

					<div className="tasks-modal-footer">
						<button type="button" className="btn btn-outline-secondary btn-wide"
							onClick={() => toggleModalCreateUpdate()}>
							{t('cancel')}
						</button>
						<button type="submit" className="btn btn-success btn-wide"
							disabled={!selectedItem.name}>
							<i className="uil uil-check me-1"></i>
							{!selectedItem.id ? t('task_create') : t('save')}
						</button>
					</div>
				</form>
			</Modal.Body>
		</Modal>
	)
}

export default ModalCreateUpdate
